import { Link } from 'react-router-dom';
import { usePermission } from '@features/auth/hooks/usePermission';
import { hasPermission, PERMISSIONS } from '@features/auth/utils/permissionRules';
import { Logo } from '@core/components/ui/Logo';
import { NAVIGATION_CONFIG } from '../../routing/navigationConfig';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarNavSection } from './SidebarNavSection';
import { SidebarChatSection } from './SidebarChatSection';

/**
 * Main navigation component for the sidebar
 * Renders the logo (when collapsed), main items, and navigation sections
 * Filters sections based on user permissions using hierarchical permission system
 */
export function SidebarNavigation() {
  const { user } = usePermission();

  // Check if user is a superadmin
  const isSuperAdmin = hasPermission(user?.groups, PERMISSIONS.CROSS_ACCOUNT_ACCESS);

  // Filter sections based on user permissions with automatic hierarchy
  const visibleSections = NAVIGATION_CONFIG.sections.filter((section) => {
    // If no permission specified, section is accessible to all authenticated users
    if (!section.permission) {
      return true;
    }

    // If user is superadmin, hide sections marked with hideForSuperadmin (only show superadmin section)
    if (isSuperAdmin && section.hideForSuperadmin) {
      return false;
    }

    // Check permission with automatic admin/superadmin inheritance
    return hasPermission(user?.groups, section.permission);
  });

  return (
    <div className="h-full overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-zinc-100 [&::-webkit-scrollbar-thumb]:bg-zinc-300">
      <nav
        className="hs-accordion-group p-3 w-full flex flex-col flex-wrap"
        data-hs-accordion-always-open
      >
        {/* Logo when collapsed */}
        <div className="hidden hs-overlay-minified:block mb-3">
          <Link
            to="/"
            className="min-h-[36px] flex items-center justify-center focus:outline-none focus:opacity-80"
          >
            <Logo variant="compact" className="h-6 w-auto text-brand" />
          </Link>
        </div>

        <ul className="flex flex-col space-y-1">
          {/* Main Navigation Items */}
          {NAVIGATION_CONFIG.main.map((item) => (
            <SidebarNavItem
              key={item.path}
              path={item.path}
              labelKey={item.labelKey}
              icon={item.icon}
            />
          ))}

          {/* Chat History Section */}
          <SidebarChatSection />

          {/* Navigation Sections - Filtered by user permissions */}
          {visibleSections.map((section, index) => (
            <SidebarNavSection
              key={`section-${section.titleKey}-${index}`}
              titleKey={section.titleKey}
              items={section.items}
              userGroups={user?.groups}
            />
          ))}
        </ul>
      </nav>
    </div>
  );
}
